import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useCompany from "../../../hooks/useCompany";
import useModal from "../../../hooks/useModal";
import useAPI from "../../../hooks/useAPI";
import { Button } from "@oc-labs/wt-storybook";
import toast from "react-hot-toast";
import DeleteModal from "../../../components/modals/DeleteModal";
import AddUserModal from "./components/AddUserModal";
import { Plus, KeyRound, Calendar, Coins, ArrowLeftRight } from "lucide-react";

const ALL_MODULES = [
  {
    id: "331041a2-6be2-4d3b-9468-fb4d7442c78a",
    name: "API Key Module",
    slug: "api_key_module",
  },
  {
    id: "797174cf-974a-410d-8768-db7de1fe7ab3",
    name: "Event Module",
    slug: "event_module",
  },
  {
    id: "fd37d818-7a41-4b56-9f5b-bf2abf7157ac",
    name: "RWA Module",
    slug: "rwa_module",
  },
  {
    id: "d676ecb5-f283-48b1-a57f-79c95e378c57",
    name: "Swap Module",
    slug: "swap_module",
  },
  {
    id: "b8e4f3c1-5a2d-4e9f-8c7b-1d3f5e7a9c0b",
    name: "SSO Module",
    slug: "sso_module",
  },
];

export default function AdminCompaniesInfo() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { open: openDeleteModal } = useModal("delete-modal");
  const { open: openAddUserModal } = useModal("add-user");
  const { updateCompany, refetchCompanies, getCompanyDetails, deleteCompany, activeCompany, refetchActiveCompany, getCompanyUsers } = useCompany();
  const api = useAPI();
  
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyModules, setCompanyModules] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);

  const [isEditingName, setIsEditingName] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState(company?.name || "");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (companyId) {
      fetchCompanyDetails();
    }
  }, [companyId]);

  useEffect(() => {
    setNewCompanyName(company?.name || "");
    const modulesArray = company?.modules || [];
    const fullModules = modulesArray.map(moduleSlug => {
      if (typeof moduleSlug === 'object') return moduleSlug;
      return ALL_MODULES.find(m => m.slug === moduleSlug);
    }).filter(Boolean);
    setCompanyModules(fullModules);
  }, [company]);

  const fetchCompanyDetails = async () => {
    setLoading(true);
    try {
      const details = await getCompanyDetails(companyId);
      setCompany(details);
      
      // Fetch company users separately
      const users = await getCompanyUsers(companyId);
      setCompanyUsers(users || []);
    } catch (error) {
      console.error("Error fetching company details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCompany = () => {
    openDeleteModal({
      title: "Delete Company",
      text: `Are you sure you want to delete "${company?.name}"? This action cannot be undone.`,
      onDelete: async () => {
        try {
          await deleteCompany(companyId);
          toast.success("Company deleted successfully");
          setTimeout(() => navigate("/admin/companies"), 500);
        } catch (error) {
          console.error("Error deleting company:", error);
          toast.error("Failed to delete company");
        }
      }
    });
  };

  const onUserAdded = async () => {
    await fetchCompanyDetails();
  };

  const handleRemoveUser = async (userId) => {
    try {
      await api.adminCompanyRemoveUser(companyId, userId);
      toast.success("User removed successfully");
      await fetchCompanyDetails();
    } catch (error) {
      console.error("Error removing user:", error);
      toast.error("Failed to remove user");
    }
  };

  if (loading) return (
    <div className="p-6">Loading company details...</div>
  );

  if (!company) return (
    <div className="p-6">No company data found.</div>
  );

  const handleUpdateCompanyName = async () => {
    if (!newCompanyName.trim()) return;
    setIsSaving(true);
    try {
      await updateCompany(company.id, { name: newCompanyName });
      setIsEditingName(false);
      await refetchCompanies();
      toast.success("Company name updated");
      await fetchCompanyDetails();
    } catch (error) {
      console.error("Error updating company name:", error);
      setNewCompanyName(company.name);
      toast.error("Failed to update company name");
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleModule = async (moduleId) => {
    const isActive = companyModules.some((m) => m.id === moduleId);
    const module = ALL_MODULES.find((m) => m.id === moduleId);
    if (!module) return;

    try {
      if (isActive) {
        await api.disableCompanyModule(company.id, module.slug);
        setCompanyModules(companyModules.filter((m) => m.id !== moduleId));
      } else {
        await api.enableCompanyModule(company.id, module.slug);
        setCompanyModules([...companyModules, module]);
      }
      toast.success(isActive ? "Module deactivated" : "Module activated");
      if (activeCompany?.id === company.id) {
        await refetchActiveCompany();
      }
    } catch (error) {
      console.error("Error updating modules:", error);
      toast.error(error?.response?.data?.message || "Failed to update module");
    }
  };

  const MODULES_META = {
    api_key_module: {
      bg: "bg-blue-50",
      text: "text-blue-600",
      description: "Manage developer credentials and control secure API access.",
      icon: "Key" 
    },
    event_module: {
      bg: "bg-purple-50",
      text: "text-blue-600",
      description: "Discover and join real-world events and experiences.",
      icon: "Calendar" 
    },
    rwa_module: {
      bg: "bg-emerald-50",
      text: "text-blue-600",
      description: "Tokenize real-world assets into NFTs with built-in compliance tools.",
      icon: "Coins"
    },
    swap_module: {
      bg: "bg-yellow-50",
      text: "text-blue-600",
      description: "Enable seamless token swapping with integrated liquidity providers.",
      icon: "ArrowLeftRight"
    },
    sso_module: {
      bg: "bg-indigo-50",
      text: "text-blue-600",
      description: "Configure Single Sign-On providers for federated authentication.",
      icon: "ShieldCheck"
    }
  };


  return (
    <div className="min-h-screen text-slate-900 font-body">
      <main className="mx-auto px-4 sm:px-6 lg:px-8">
        {/* TÍTULO Y ACCIONES */}
        <div className="flex flex-col md:flex-row md:items-center justify-end mb-8 gap-4">
          
          <div className="flex items-center space-x-3">
            <Button              
              onClick={handleDeleteCompany}
              size="small"
              className="!bg-red-600 hover:!bg-red-700 text-white"
            >
              Delete Company
            </Button>
          </div>
        </div>

        {/* CARD INFO */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              {isEditingName ? (
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={newCompanyName}
                    onChange={(e) => setNewCompanyName(e.target.value)}
                    className="text-xl font-bold border-b-2 border-blue-500 bg-transparent focus:outline-none"
                  />
                  <Button
                    size="small"
                    type="primary"
                    onClick={handleUpdateCompanyName}
                  >
                    Save
                  </Button>

                  <Button
                    size="small"
                    type="tertiary"
                    onClick={() => {
                      setIsEditingName(false)
                      setNewCompanyName(company.name)
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-bold text-gray-900">{company.name}</h1>
                  <button onClick={() => setIsEditingName(true)} className="text-blue-600 hover:text-blue-800 text-sm">
                    Edit
                  </button>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-8">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Company Identifier</p>
                <div className="flex items-center space-x-2">
                  <code className="text-sm font-mono text-slate-900 bg-slate-100 px-2 py-0.5 rounded">{company.id}</code>
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Created Date</p>
                <p className="text-sm font-semibold flex items-center">
                  {company.createdAt ? new Date(company.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : "-"}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Last Updated</p>
                <p className="text-sm font-semibold flex items-center">
                  {company.updatedAt ? new Date(company.updatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : "-"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* MÓDULOS */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          {ALL_MODULES.map((module) => {
            const meta = MODULES_META[module.slug] || {};
            const isActive = companyModules.some((m) => m.id === module.id);
            return (
              <div key={module.id} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="sr-only peer" 
                      checked={isActive} 
                      onChange={() => handleToggleModule(module.id)}
                    />
                    <div className="w-11 h-6 bg-gray-300 rounded-full peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full peer-checked:after:border-white"></div>
                  </label>
                </div>
                <div className="mt-4">
                    <div className="flex items-center gap-2">
                      {meta.icon === "Key" && <KeyRound className={`w-5 h-5 ${meta.text}`} />}
                      {meta.icon === "Calendar" && <Calendar className={`w-5 h-5 ${meta.text}`} />}
                      {meta.icon === "Coins" && <Coins className={`w-5 h-5 ${meta.text}`} />}
                      {meta.icon === "ArrowLeftRight" && <ArrowLeftRight className={`w-5 h-5 ${meta.text}`} />}
                      <h3 className="font-bold text-slate-900">{module.name}</h3>
                    </div>
                  <p className="text-sm text-slate-500 mt-1 leading-relaxed">{meta.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* USUARIOS */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Users ({companyUsers.length})</h2>

              <div>
                <Button
                  type="secondary"
                  size="small"
                  className="whitespace-nowrap"
                  icon={<Plus size={16} />}
                  onClick={() => openAddUserModal()}
                >
                  Add User
                </Button>
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div>
            <table className="w-full text-sm table-fixed">

              {/* Table Head */}
              <thead className="bg-slate-50 border-b border-slate-200 text-left">
                <tr className="text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-6 py-4 font-medium w-[25%]">User</th>
                  <th className="px-6 py-4 font-medium w-[30%]">Contact</th>
                  <th className="px-6 py-4 font-medium w-[30%]">Personal Info</th>
                  <th className="px-6 py-4 font-medium text-right w-[15%]">Action</th>
                </tr>
              </thead>

              {/* Table Body */}
              <tbody className="divide-y divide-slate-100">
                {companyUsers?.map((user) => (
                  <tr
                    key={user.id}
                    className="hover:bg-slate-50 transition-colors"
                  >
                    {/* USER COLUMN */}
                    <td className="px-6 py-5 min-w-0">
                      <div className="flex flex-col min-w-0">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="font-semibold text-slate-900 truncate">
                            {user.name}
                          </span>

                          {user.isVerified && (
                            <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded-full font-medium shrink-0">
                              Verified
                            </span>
                          )}
                        </div>

                        <span className="text-slate-500 text-xs mt-1 truncate">
                          ID: {user.id}
                        </span>
                      </div>
                    </td>

                    {/* CONTACT COLUMN */}
                    <td className="px-6 py-5 min-w-0">
                      <div className="flex flex-col gap-1 text-slate-700 min-w-0">
                        <span className="truncate">
                          <span className="text-slate-400 text-xs uppercase mr-1">Email</span>
                          {user.email}
                        </span>

                        <span className="truncate">
                          <span className="text-slate-400 text-xs uppercase mr-1">Phone</span>
                          {user.phoneNumber || "N/A"}
                        </span>

                        <span className="truncate">
                          <span className="text-slate-400 text-xs uppercase mr-1">Lang</span>
                          {user.preferredLanguage || "N/A"}
                        </span>
                      </div>
                    </td>

                    {/* PERSONAL INFO COLUMN */}
                    <td className="px-6 py-5 min-w-0">
                      <div className="grid grid-cols-1 gap-1 text-slate-700 min-w-0">
                        <span className="truncate">
                          <span className="text-slate-400 text-xs uppercase mr-1">Given</span>
                          {user.name || "N/A"}
                        </span>

                        <span className="truncate">
                          <span className="text-slate-400 text-xs uppercase mr-1">Family</span>
                          {user.familyName || "N/A"}
                        </span>

                        <span className="truncate">
                          <span className="text-slate-400 text-xs uppercase mr-1">Nick</span>
                          {user.nickName || "N/A"}
                        </span>
                      </div>
                    </td>

                    {/* ACTION */}
                    <td className="px-6 py-5 text-right">
                      <Button
                        onClick={() => handleRemoveUser(user.id)}
                        size="small"
                        className="!bg-red-600 hover:!bg-red-700 text-white"
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}

                {(!companyUsers || companyUsers.length === 0) && (
                  <tr>
                    <td colSpan="4" className="p-12 text-center text-slate-500">
                      <p>No users found for this company.</p>
                      <button
                        onClick={() => openAddUserModal()}
                        className="mt-2 text-blue-600 hover:text-blue-800"
                      >
                        Add the first user
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
      
      <DeleteModal />
      <AddUserModal companyId={companyId} onUserAdded={onUserAdded} />
    </div>
  );
}
